/**
 * Version-poll: detects when a new build has been deployed and
 * gracefully reloads the page so the user picks up the latest
 * JS/CSS bundles without a manual hard refresh.
 *
 * Mechanism (Feb 2026):
 *   1. At boot, fetch the SPA's `index.html` with a cache-buster
 *      query string and store a hash of the entire body. This is
 *      the "this is the version the user originally loaded" anchor.
 *   2. Every POLL_INTERVAL_MS, re-fetch `index.html` with a fresh
 *      cache-buster and re-hash the body.
 *   3. If the new hash differs from the original, the SPA shell
 *      has changed — a new deploy happened. Trigger a
 *      `window.location.reload(true)` so the browser drops any
 *      stale cached chunks and pulls the fresh build.
 *
 * Why hash index.html and not a separate `/api/build-version`
 * endpoint?
 *   • Zero backend change required — works against any static host.
 *   • The actual file the browser caches IS index.html (the entry
 *     point into the hashed JS/CSS chunks), so hashing it is the
 *     most accurate "did the build change?" signal.
 *   • If only the JS chunks changed (which never happens with CRA
 *     — index.html always changes when chunks do, because their
 *     hashed filenames change), index.html still changes and we
 *     still detect it.
 *
 * Why only reload during idle (visibility=visible) moments?
 *   • Reloading mid-task (mid-typing in chat, mid-intake form)
 *     would lose the user's in-progress work. We only reload when
 *     the tab becomes visible AND the user hasn't interacted in
 *     the last 30 seconds, on the assumption that they're idle.
 *
 * Graceful fallback:
 *   • Any fetch error → silent retry on the next interval.
 *   • Cross-origin fetch failure → poll is a no-op (we never get
 *     a hash mismatch, so no reload is triggered).
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const IDLE_THRESHOLD_MS = 30 * 1000;     // 30 seconds since last input
const VERSION_KEY = "rm_app_version_hash";
const SESSION_RELOAD_KEY = "rm_version_reload_attempt_ts";

/** Hash a string with a deterministic 32-bit FNV-1a variant. We do
 * NOT need crypto strength here — just a stable change-detection
 * signal. FNV-1a is tiny and avoids the SubtleCrypto async API. */
function _hash(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(16);
}

async function _fetchShellHash() {
  // Cache-buster query string forces the browser to bypass any
  // intermediary cache. We also send `cache: "no-store"` for good
  // measure on supporting browsers.
  const buster = `?_v=${Date.now()}`;
  try {
    const res = await fetch(`/index.html${buster}`, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const text = await res.text();
    // The dynamic posthog distinct-id and any timestamp-y attributes
    // in the HTML shell are NOT present — CRA's index.html is fully
    // static at build time, so hashing the entire response is safe.
    return _hash(text);
  } catch (_e) {
    return null;
  }
}

let _lastInputTs = Date.now();
function _markInteraction() { _lastInputTs = Date.now(); }

function _isIdle() {
  return (
    document.visibilityState === "visible" &&
    Date.now() - _lastInputTs > IDLE_THRESHOLD_MS
  );
}

function _maybeReload() {
  // Avoid an infinite reload loop if our anchor hash is somehow
  // corrupt: a reload triggered in the last 60s blocks the next one.
  let lastReload = 0;
  try {
    lastReload = parseInt(sessionStorage.getItem(SESSION_RELOAD_KEY) || "0", 10);
  } catch (_e) { /* no-op */ }
  if (Date.now() - lastReload < 60 * 1000) return;
  try {
    sessionStorage.setItem(SESSION_RELOAD_KEY, String(Date.now()));
  } catch (_e) { /* no-op */ }
  // The "true" arg is deprecated but historically forces a hard
  // reload bypassing the cache. Modern browsers honor it; older
  // ones fall back to standard reload semantics (still respects our
  // no-cache meta tags).
  window.location.reload();
}

export function startVersionPoll() {
  if (typeof window === "undefined") return () => {};

  ["click", "keydown", "scroll", "touchstart", "pointermove"].forEach((evt) =>
    window.addEventListener(evt, _markInteraction, { passive: true })
  );

  let cancelled = false;
  let anchorHash = null;

  // Capture the anchor hash on first run.
  _fetchShellHash().then((h) => {
    if (!h || cancelled) return;
    anchorHash = h;
    try { sessionStorage.setItem(VERSION_KEY, h); } catch (_e) { /* no-op */ }
  });

  const interval = setInterval(async () => {
    if (cancelled || !anchorHash) return;
    const current = await _fetchShellHash();
    if (!current || current === anchorHash) return;
    // New build detected — reload at the next idle moment.
    if (_isIdle()) {
      _maybeReload();
    } else {
      // Wait until idle, then reload.
      const wait = () => {
        if (cancelled) return;
        if (_isIdle()) _maybeReload();
        else setTimeout(wait, 5000);
      };
      setTimeout(wait, 5000);
    }
  }, POLL_INTERVAL_MS);

  return () => {
    cancelled = true;
    clearInterval(interval);
    ["click", "keydown", "scroll", "touchstart", "pointermove"].forEach((evt) =>
      window.removeEventListener(evt, _markInteraction)
    );
  };
}
